import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, ArrowRight, Flame, Sparkles, ShieldCheck, Truck } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Countdown from "@/components/Countdown";
import { emojiFor } from "@/lib/categories";

export default function Home() {
  const [flash, setFlash] = useState([]);
  const [popular, setPopular] = useState([]);
  const [cats, setCats] = useState([]);
  const [distribuidor, setDistribuidor] = useState([]);
  const [perfumes, setPerfumes] = useState([]);

  useEffect(() => {
    // El home muestra cada línea en su propia estantería: el catálogo CJ no se
    // mezcla con las marcas que distribuimos ni con la perfumería local.
    api.get("/products", { params: { flash: true, limit: 8, product_line: "cj" } }).then((r) => setFlash(r.data));
    api.get("/products", { params: { sort: "popular", limit: 12, product_line: "cj" } }).then((r) => setPopular(r.data));
    api.get("/products", { params: { limit: 12, product_line: "distribuidor_oficial" } }).then((r) => setDistribuidor(r.data));
    api.get("/products", { params: { limit: 12, product_line: "perfumes_local" } }).then((r) => setPerfumes(r.data));
    api.get("/categories", { params: { product_line: "cj" } }).then((r) => setCats(r.data));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-3 md:px-6">
      {/* Hero */}
      <section className="mt-4 rounded-2xl overflow-hidden relative bg-gradient-to-br from-drelo-ink via-[#1e293b] to-drelo-ink text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url('https://images.pexels.com/photos/8904066/pexels-photo-8904066.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative p-7 md:p-14 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 bg-drelo-urgency text-white text-xs font-black px-3 py-1.5 rounded-full mb-4 animate-drelo-pulse">
            <Flame className="w-3.5 h-3.5" /> OFERTA RELÁMPAGO
          </span>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-[0.95]">
            Wellness que <span className="text-drelo-primary">sí</span> sientes
          </h1>
          <p className="mt-4 text-white/70 text-base md:text-lg">Hasta <span className="text-drelo-deal font-bold">-70%</span> en cuidado personal, suplementos y bienestar. Stock limitado.</p>
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <span className="text-sm text-white/70">Termina en</span>
            <Countdown hoursFromNow={5} light testId="hero-countdown" />
          </div>
          <Link to="/tienda" className="mt-6 inline-flex items-center gap-2 bg-drelo-primary hover:bg-drelo-hover text-white font-black uppercase tracking-wide px-7 py-3.5 rounded-full transition-all active:scale-95" data-testid="hero-cta">
            Comprar ahora <ArrowRight className="w-5 h-5" />
          </Link>
          <div className="mt-6 flex gap-5 text-xs text-white/60">
            <span className="flex items-center gap-1.5"><Truck className="w-4 h-4" /> Envío gratis +$35</span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Pago seguro</span>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="mt-8">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {cats.slice(0, 12).map((c) => (
            <Link key={c} to={`/tienda?category=${encodeURIComponent(c)}`}
              className="bg-white rounded-xl border border-slate-100 p-4 text-center hover:border-drelo-primary hover:-translate-y-1 transition-all" data-testid={`cat-${c}`}>
              <div className="text-3xl mb-1">{emojiFor(c)}</div>
              <div className="text-xs font-bold leading-tight">{c}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Flash deals */}
      <section className="mt-10">
        <div className="flex items-center justify-between mb-4 bg-drelo-urgency text-white rounded-xl px-4 py-3">
          <h2 className="font-display font-black text-xl md:text-2xl flex items-center gap-2 uppercase tracking-tight">
            <Zap className="w-6 h-6 text-drelo-deal fill-drelo-deal" /> Ofertas Relámpago
          </h2>
          <div className="flex items-center gap-2 text-sm font-bold">
            <span className="hidden sm:inline">Acaba en</span>
            <Countdown hoursFromNow={5} light testId="flash-countdown" />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {flash.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* Distribución oficial — Climaplex / Hairmed */}
      {distribuidor.length > 0 && (
        <section className="mt-12" data-testid="line-distribuidor">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight">Distribución oficial</h2>
              <p className="text-drelo-muted text-sm mt-1">Marcas que distribuimos en República Dominicana. Stock local.</p>
            </div>
            <Link to="/tienda?linea=distribuidor_oficial" className="text-sm font-black uppercase text-drelo-primary hover:underline shrink-0">Ver todo</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {distribuidor.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* Perfumería local */}
      {perfumes.length > 0 && (
        <section className="mt-12" data-testid="line-perfumes">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight">Perfumería</h2>
              <p className="text-drelo-muted text-sm mt-1">Fragancias con stock local y entrega rápida.</p>
            </div>
            <Link to="/tienda?linea=perfumes_local" className="text-sm font-black uppercase text-drelo-primary hover:underline shrink-0">Ver todo</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {perfumes.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* Ambassador banner */}
      <section className="mt-12 rounded-2xl bg-gradient-to-r from-drelo-primary to-drelo-deal p-7 md:p-10 text-white relative overflow-hidden">
        <Sparkles className="absolute right-4 top-4 w-24 h-24 opacity-20" />
        <h2 className="font-display font-black text-2xl md:text-4xl uppercase tracking-tight">Conviértete en Embajador Drelo</h2>
        <p className="mt-2 text-white/90 max-w-xl">Comparte tu código, gana 10% de comisión por cada venta y desbloquea regalos por volumen. Sin límites.</p>
        <Link to="/embajadores" className="mt-5 inline-flex items-center gap-2 bg-white text-drelo-ink font-black uppercase px-6 py-3 rounded-full hover:scale-105 transition-transform" data-testid="ambassador-banner-cta">
          Unirme gratis <ArrowRight className="w-5 h-5" />
        </Link>
      </section>

      {/* Popular */}
      <section className="mt-12">
        <h2 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight mb-4">🔥 Lo más vendido</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {popular.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
        <div className="text-center mt-8">
          <Link to="/tienda" className="inline-flex items-center gap-2 border-2 border-drelo-ink font-black uppercase px-7 py-3 rounded-full hover:bg-drelo-ink hover:text-white transition-all" data-testid="see-all-btn">
            Ver toda la tienda <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
